import React from 'react';
import { createPortal } from 'react-dom';
import AegisAssistWidget from '../../../Components/AegisAssistWidget/AegisAssistWidget';
import WidgetMarkdown from '../../../Components/AegisAssistWidget/WidgetMarkdown';
import { collectWidgetCss } from '../../../Components/AegisAssistWidget/genie';
import { clearAegisAiChatSession, streamAegisAiChatMessage } from '../../../Services/AegisAiChatService';
import { baseurl } from '../../../Utils/Constants';

const RAG_IMAGE_PATH = /\/api\/aegisai\/rag\/image\/([^/?#\s]+)/;
const Z_INDEX = 1055;

// Mounts point for the trial inside a Shadow DOM, exactly like the embed script on client sites: the
// builder page's global CSS (buttons, textareas, ...) can't restyle the widget, so the trial looks the
// way clients will see it and matches the genie snapshot pixel for pixel.
function useShadowMount() {
    const [mount, setMount] = React.useState(null);

    React.useLayoutEffect(() => {
        const css = collectWidgetCss(document.body);
        if (!css.includes('.aaw-panel') || !document.body.attachShadow) {
            setMount(document.body); // stylesheet unreadable: render in the page as before
            return undefined;
        }
        const host = document.createElement('div');
        host.setAttribute('data-aegis-assist-trial', '');
        host.style.cssText = `all: initial; position: fixed; top: 0; left: 0; width: 0; height: 0; z-index: ${Z_INDEX};`;
        const shadow = host.attachShadow({ mode: 'open' });
        const style = document.createElement('style');
        style.textContent = css;
        const container = document.createElement('div');
        shadow.append(style, container);
        document.body.appendChild(host);
        setMount(container);
        return () => host.remove();
    }, []);

    return mount;
}

// Live trial of the client widget inside the builder: same component the embed script ships,
// streaming real answers from the selected client's knowledge base with the admin's session.
export default function AssistTrialWidget({ token, clientId, fallbackMessage, title, ...widgetProps }) {
    const mount = useShadowMount();

    const sendMessage = React.useCallback(
        (query, handlers, signal, context) => streamAegisAiChatMessage(token, {
            query,
            client_id: clientId,
            fallback_message: fallbackMessage || undefined,
            // The trial answers as the agent being built, exactly like the client's website will.
            assistant_name: title,
            client_time: context?.clientTime,
            session_id: context?.sessionId,
        }, handlers, signal),
        [token, clientId, fallbackMessage, title],
    );

    const endSession = React.useCallback((sessionId) => clearAegisAiChatSession(token, sessionId), [token]);

    // Answer images are proxied by the API; <img> can't send the bearer header, so pass it as ?token=.
    const renderContent = React.useCallback(({ content, isStreaming }) => (
        <WidgetMarkdown
            content={content}
            isStreaming={isStreaming}
            resolveImageUrl={(url) => {
                const match = String(url || '').match(RAG_IMAGE_PATH);
                if (!match) return url;
                return `${baseurl}/aegisai/rag/image/${match[1]}?token=${encodeURIComponent(token || '')}`;
            }}
        />
    ), [token]);

    if (!mount) return null;

    return createPortal(
        <AegisAssistWidget
            {...widgetProps}
            fallbackMessage={fallbackMessage}
            title={title}
            sendMessage={sendMessage}
            onSessionEnd={endSession}
            renderContent={renderContent}
            zIndex={Z_INDEX}
        />,
        mount,
    );
}
